import CloseButton from 'Components/Common/CloseButton'
import SingleSlider from 'Components/Common/SingleSlider'
import CheckboxGroup from 'Components/input/CheckboxGroup/CheckboxGroup'
import RadioGroup from 'Components/input/RadioGroup/RadioGroup'
import { genders } from 'constants/campaign'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Offcanvas, OffcanvasBody, OffcanvasHeader } from 'reactstrap'
import { RootState } from 'store'
import { ApplicationFiltersNew, SortOptions } from 'types/campaign/applications'

interface ApplicationFiltersProps {
    isOpen: boolean,
    onClose: () => void,
    defaultFilters: ApplicationFiltersNew
    onApply: (filters: ApplicationFiltersNew) => void;
}

export const defaultApplicationFilters: ApplicationFiltersNew = {
    // min_requirement_match: ScoreLevel.BELOW_AVERAGE,
    // min_authenticity_score: ScoreLevel.AVERAGE, 
    declined: 'hide',
    page: 1,
    sort: SortOptions.MATCH_PERCENTAGE,
    q: ''
}

const ApplicationFilters: React.FC<ApplicationFiltersProps> = ({ isOpen, onClose, defaultFilters, onApply }) => {
    const [filters, setFilters] = useState(defaultFilters);

    const { campaignId } = useParams();

    const campaign = useSelector((state: RootState) => state.campaigns.items[campaignId!]?.data?.campaign);
    const niches = useSelector((state: RootState) => state.auth.master?.niche_list);
    const specialityNiches = useSelector((state: RootState) => state.auth.master?.speciality_niches);
    const languages = useSelector((state: RootState) => state.master.languages);
    const connectionRequired = useSelector((state: RootState) => state.campaigns.items[campaignId!]?.data?.campaign?.connection_required);
    useEffect(() => {
        setFilters(defaultFilters)
    }, [defaultFilters])

    return (
        <Offcanvas isOpen={isOpen} toggle={onClose} direction='end'>
            <CloseButton onClose={onClose} />
            {/* <OffcanvasHeader>Application Filters</OffcanvasHeader> */}
            <OffcanvasBody className='p-0 position-relative'>
                <div className="payments-offcanvas-bg"></div>
                <div className="filters p-4">
                    <div className="form-group filter-item">
                        <label htmlFor="gender" className="filter-label">Gender {campaign?.requirements?.gender ? `(${campaign.requirements.gender.toUpperCase()})` : ''}</label>
                        <RadioGroup
                            name="gender"
                            options={Object.entries(genders).map(([key, value]) => ({ id: key, label: value }))}
                            defaultValue={filters.gender ?? ''}
                            onChange={(gender) => {
                                setFilters({ ...filters, gender })
                            }}
                            selectFirstWhenNoValueProvided={false}
                        />
                    </div>
                    <div className="form-group filter-item">
                        <label htmlFor="age" className="filter-label">Creator Age {campaign?.requirements?.age?.min ? `(${campaign.requirements.age.min}-${campaign.requirements.age.max})` : ''}</label>
                        <div className="d-flex gap-3">
                            <select className="form-select w-50"
                                value={filters?.age_min ?? ''}
                                onChange={(e) => setFilters({ ...filters, age_min: Number(e.target.value) })}
                                name="age-min"
                            >
                                <option value="">From</option>
                                {Array.from({ length: 87 }, (_, i) => i + 13).map((age) => {
                                    if (filters?.age_max && age > Number(filters.age_max)) return null;
                                    return (
                                        <option key={age} value={age}>
                                            {age}
                                        </option>
                                    )
                                })}
                            </select>
                            <select className="form-select w-50"
                                value={filters?.age_max ?? ''}
                                onChange={(e) => setFilters({ ...filters, age_max: Number(e.target.value) })}
                                name="age-max"
                            >
                                <option value="">To</option>
                                {Array.from({ length: 87 }, (_, i) => i + 13).map((age) => {
                                    if (filters?.age_min && age < Number(filters.age_min)) return null
                                    return (
                                        <option key={age} value={age}>
                                            {age}
                                        </option>
                                    )
                                })}
                            </select>
                        </div>
                    </div>
                    <div className="form-group filter-item">
                        <label htmlFor="min-ig-followers" className="filter-label">Minimum Followers/Subscribers</label>
                        <input
                            type="number"
                            value={filters.follower_count ? filters.follower_count : ""}
                            className='form-control'
                            placeholder={`${campaign?.requirements?.follower_count?.min ?? 1}`}
                            onChange={(e) => setFilters({ ...filters, follower_count: e.target.value })}
                        />
                    </div>
                    <div className="form-group filter-item">
                        <label htmlFor="min-ig-followers" className="filter-label">Minimum Average Views</label>
                        <input
                            type="number"
                            value={filters.average_views ? filters.average_views : ""}
                            className='form-control'
                            placeholder={`${campaign?.requirements?.average_views?.min ?? 1}`}
                            onChange={(e) => setFilters({ ...filters, average_views: e.target.value })}
                        />
                    </div>
                    {
                        (campaign?.requirements?.niches?.length ?? 0) > 0 &&
                        <div className="form-group filter-item">
                            <label htmlFor="niches" className="filter-label">Creator Interests</label>
                            <CheckboxGroup
                                options={(campaign?.requirements?.niches ?? []).map((nicheId) => ({ label: niches?.[nicheId] ?? "-", value: nicheId }))}
                                selectedValues={filters.niches ?? []}
                                onChange={(selectedValues) => setFilters({ ...filters, niches: (selectedValues.map((niche) => String(niche))) })}
                            />
                        </div>
                    }
                    {
                        (campaign?.requirements?.speciality_niches?.length ?? 0) > 0 &&
                        <div className="form-group filter-item">
                            <label htmlFor="speciality_niches" className="filter-label">Creator Interests</label>
                            <CheckboxGroup
                                options={(campaign?.requirements?.speciality_niches ?? []).map((nicheId) => ({ label: (specialityNiches ?? [])?.find((niche) => Number(niche.id) === Number(nicheId))?.name ?? "-", value: nicheId }))}
                                selectedValues={filters.speciality_niches ?? []}
                                onChange={(selectedValues) => setFilters({ ...filters, speciality_niches: (selectedValues.map((niche) => niche)) })}
                            />
                        </div>
                    }
                    {
                        (campaign?.requirements?.cities?.length ?? 0) > 0 &&
                        <div className="form-group filter-item">
                            <label htmlFor="cities" className="filter-label">City:</label>
                            <CheckboxGroup
                                options={(campaign?.requirements?.cities ?? []).map((city) => ({ label: city, value: city }))}
                                selectedValues={filters.cities ?? []}
                                onChange={(selectedValues) => setFilters({ ...filters, cities: (selectedValues.map((city) => String(city))) })}
                            />
                        </div>
                    }
                    {
                        (campaign?.requirements?.regions?.length ?? 0) > 0 &&
                        <div className="form-group filter-item">
                            <label htmlFor="regions" className="filter-label">Region:</label>
                            <CheckboxGroup
                                options={(campaign?.requirements?.regions ?? []).map((region) => ({ label: region, value: region }))}
                                selectedValues={filters.regions ?? []}
                                onChange={(selectedValues) => setFilters({ ...filters, regions: (selectedValues.map((region) => String(region))) })}
                            />
                        </div>
                    }
                    {
                        (campaign?.requirements?.languages?.length ?? 0) > 0 &&
                        <div className="form-group filter-item">
                            <label htmlFor="languages" className="filter-label">Languages</label>
                            <CheckboxGroup
                                options={(campaign?.requirements?.languages ?? []).map((languageId) => ({ label: languages?.[languageId] ?? "-", value: languageId }))}
                                selectedValues={filters.languages ?? []}
                                onChange={(selectedValues) => setFilters({ ...filters, languages: (selectedValues.map((language) => String(language))) })}
                            />
                        </div>
                    }
                    {
                        (campaign?.requirements?.topics?.length ?? 0) > 0 &&
                        <div className="form-group filter-item">
                            <label htmlFor="niches" className="filter-label">Have Topic:</label>
                            <CheckboxGroup
                                options={(campaign?.requirements?.topics ?? []).map((topic) => ({ label: topic, value: topic }))}
                                selectedValues={filters.topics ?? []}
                                onChange={(selectedValues) => setFilters({ ...filters, topics: (selectedValues.map((topic) => String(topic))) })}
                            />
                        </div>
                    }
                    {
                        (campaign?.requirements?.exclude_topics?.length ?? 0) > 0 &&
                        <div className="form-group filter-item">
                            <label htmlFor="niches" className="filter-label">Doesn't Have Topic:</label>
                            <CheckboxGroup
                                options={(campaign?.requirements?.exclude_topics ?? []).map((topic) => ({ label: topic, value: topic }))}
                                selectedValues={filters.exclude_topics ?? []}
                                onChange={(selectedValues) => setFilters({ ...filters, exclude_topics: (selectedValues.map((topic) => String(topic))) })}
                            />
                        </div>
                    }
                    {
                        campaign?.requirements?.audience_gender !== 'A' && connectionRequired &&
                        <div className="form-group filter-item gap-3">
                            <div className='d-flex flex-column gap-2'>
                                <label htmlFor="audience-gender" className="filter-label">Audience Gender {campaign?.requirements?.audience_gender ? `(${campaign.requirements.audience_gender.toUpperCase()})` : ''}</label>
                                <RadioGroup
                                    name="audience-gender"
                                    options={Object.entries(genders).map(([key, value]) => ({ id: key, label: value }))}
                                    defaultValue={filters.audience_gender ?? ''}
                                    onChange={(audience_gender) => {
                                        setFilters({ ...filters, audience_gender })
                                    }}
                                    selectFirstWhenNoValueProvided={false}
                                />
                            </div>
                            <div className=''>
                                <SingleSlider
                                    value={filters.audience_gender_threshold ?? 50}
                                    onChange={(val) => setFilters({ ...filters, audience_gender_threshold: val })}
                                    min={0}
                                    max={100}
                                    step={1}
                                    disabled={!['M', 'F'].includes(filters.audience_gender ?? '')}
                                />
                                <span className='text-muted font-12'>Minimum Audience Share (%)</span>
                            </div>
                        </div>
                    }
                    {
                        (campaign?.requirements?.audience_cities?.length ?? 0) > 0 && connectionRequired &&
                        <div className="form-group filter-item gap-3">
                            <div className="d-flex flex-column gap-2">
                                <label htmlFor="audience-cities" className="filter-label">Audience Location:</label>
                                <CheckboxGroup
                                    options={(campaign?.requirements?.audience_cities ?? []).map((city) => ({ label: city, value: city }))}
                                    selectedValues={filters.audience_cities ?? []}
                                    onChange={(selectedValues) => setFilters({ ...filters, audience_cities: (selectedValues.map((city) => String(city))) })}
                                />
                            </div>
                            <div className=''>
                                <SingleSlider
                                    value={filters.audience_cities_threshold ?? 10}
                                    onChange={(val) => setFilters({ ...filters, audience_cities_threshold: val })}
                                    min={0}
                                    max={100}
                                    step={1}
                                    disabled={!Array.isArray(filters.audience_cities ?? null) || (filters.audience_cities ?? []).length < 1 }
                                />
                                <span className='text-muted font-12'>Minimum Audience Share (%)</span>
                            </div>
                        </div>
                    }
                    {
                        (campaign?.requirements?.audience_age_groups?.length ?? 0) > 0 && connectionRequired &&
                        <div className="form-group filter-item gap-3">
                            <div className="d-flex flex-column gap-2">

                            <label htmlFor="audience-cities" className="filter-label">Audience Age:</label>
                            <CheckboxGroup
                                options={(campaign?.requirements?.audience_age_groups ?? []).map((age_group) => ({ label: age_group, value: age_group }))}
                                selectedValues={filters.audience_age_groups ?? []}
                                onChange={(selectedValues) => setFilters({ ...filters, audience_age_groups: (selectedValues.map((age_group) => String(age_group))) })}
                            />
                            </div>

                             <div className=''>
                                <SingleSlider
                                    value={filters.audience_age_groups_threshold ?? 20}
                                    onChange={(val) => setFilters({ ...filters, audience_age_groups_threshold: val })}
                                    min={0}
                                    max={100}
                                    step={1}
                                    disabled={!Array.isArray(filters.audience_age_groups ?? null) || (filters.audience_age_groups ?? []).length < 1 }
                                />
                                <span className='text-muted font-12'>Minimum Audience Share (%)</span>
                            </div>
                        </div>
                    }
                    {/* <div className="divider"></div> */}
                    {
                        connectionRequired && 
                        <div className="form-group filter-item">
                            <label htmlFor="as" className="filter-label">Authenticity Score</label>
                            <SingleSlider
                                value={filters.min_authenticity_score ?? 0}
                                onChange={(val) => setFilters({ ...filters, min_authenticity_score: val })}
                                min={0}
                                max={100}
                                step={10}
                            />
                        </div>
                    }
                    <div className="form-group filter-item">
                        <label htmlFor="age" className="filter-label">Engagement Score</label>
                        <SingleSlider
                            value={filters.min_engagement_score ?? 0}
                            onChange={(val) => setFilters({ ...filters, min_engagement_score: val })}
                            min={0}
                            max={20}
                            step={1}
                        />
                    </div>
                    <div className="form-group filter-item">
                        <label htmlFor="declined-apps" className="filter-label">Declined Applications</label>
                        <RadioGroup
                            name="declined-apps"
                            options={[{ label: "Show", id: 'show' }, { label: "Hide", id: 'hide' }]}
                            defaultValue={filters.declined ?? ''}
                            onChange={(declined) => {
                                setFilters({ ...filters, declined: declined === 'hide' ? 'hide' : 'show' })
                            }}
                            selectFirstWhenNoValueProvided={false}
                        />
                    </div>
                </div>
                <div className="filters-offcanvas-footer d-flex justify-content-between align-items-center px-3 p-4 gap-2">
                    <button className="theme-btn theme-btn-lg w-100 btn-primary-outlined"
                        onClick={() => {
                            onApply({ ...defaultApplicationFilters, page: 1, sort: filters.sort });
                            onClose();
                        }}
                    >Reset</button>
                    <button className='theme-btn theme-btn-lg w-100 btn-primary' onClick={() => {
                        onApply(filters)
                        onClose();
                    }}>Apply</button>
                </div>
            </OffcanvasBody>
        </Offcanvas>
    )
}

export default ApplicationFilters